<template>
    <div class="adaptation-block">
    <main class="adaptation-block__column my-home-page gate-home">
        <MyHomePageHeader :title="$t('mainPage.myHomeButton')" @back="$router.push({ name: 'main' })" />
        <p v-if="loading" class="gate-home__status" role="status">{{ $t('myHome.loading') }}</p>
        <div v-else-if="error" class="gate-home__status" role="alert">
            <p>{{ $t(error) }}</p>
            <button type="button" class="gate-home__retry" @click="retryKey++">{{ $t('myHome.retry') }}</button>
        </div>
        <p v-else-if="home && !home.has_access" class="gate-home__status">{{ $t('myHome.noAccess') }}</p>
        <template v-else-if="home?.has_access">
            <dl class="gate-home__address">
                <dt>{{ $t('myHome.building') }}</dt><dd>{{ home.client.building_name || home.client.complex_name || '—' }}</dd>
                <dt>{{ $t('myHome.address') }}</dt><dd>{{ home.client.address || '—' }}</dd>
            </dl>
            <button type="button" class="gate-home__accesses" @click="$router.push({ name: 'my-home-accesses' })"><span>{{ $t('myHome.myAccesses') }}</span></button>

            <section class="gate-home__section" aria-labelledby="home-biometric-title">
                <h2 id="home-biometric-title">{{ $t('myHome.biometric') }}</h2>
                <p class="gate-home__description">{{ $t('myHome.photoDescription', { limit: capacity.photoLimit }) }}</p>
                <div class="gate-home__profiles">
                    <figure v-for="(photo, index) in home.photos" :key="photo.id" class="gate-home__profile">
                        <button type="button" class="gate-home__portrait gate-home__corners" @click="$router.push('/my-home/faceid')">
                            <img v-if="images[photo.id]" :src="images[photo.id]" :alt="photo.name || $t('myHome.profile', { number: index + 1 })" @error="delete images[photo.id]">
                            <svg v-else viewBox="0 0 90 90" role="img" :aria-label="$t('myHome.photoUnavailable')">
                                <circle cx="45" cy="32" r="13" />
                                <path d="M21 76V66a24 24 0 0 1 48 0v10" />
                            </svg>
                        </button>
                        <figcaption>{{ photo.name || $t('myHome.profile', { number: index + 1 }) }}</figcaption>
                    </figure>
                    <figure v-if="capacity.canAddPhoto" class="gate-home__profile" :aria-label="$t('myHome.addProfile')">
                        <button type="button" class="gate-home__portrait gate-home__corners gate-home__plus" :aria-label="$t('myHome.addProfile')" @click="$router.push('/my-home/faceid')"><svg viewBox="0 0 90 90"><rect x="3" y="3" width="84" height="84" fill="#cacaca" fill-opacity=".1" stroke="none" /><path d="M45 28V62M28 45H62" /></svg></button>
                        <figcaption>{{ $t('myHome.addProfile') }}</figcaption>
                    </figure>
                </div>
                <p v-if="!home.photos.length && !capacity.canAddPhoto" class="gate-home__description">{{ $t('myHome.noPhotos') }}</p>
                <button type="button" class="gate-home__edit" @click="$router.push('/my-home/faceid')">{{ $t('myHome.edit') }} <svg viewBox="0 0 10 16" aria-hidden="true"><path d="M9.75 7.79422L0 0V15.5884Z" fill="currentColor" /></svg></button>
            </section>

            <section v-if="capacity.showVehicles" class="gate-home__section" aria-labelledby="home-vehicles-title">
                <h2 id="home-vehicles-title">{{ $t('myHome.vehicles') }}</h2>
                <p class="gate-home__description">{{ $t('myHome.vehicleDescription', { limit: capacity.vehicleLimit }) }}</p>
                <div class="gate-home__vehicles">
                    <figure v-for="(vehicle, index) in home.vehicles" :key="vehicle.id" class="gate-home__vehicle">
                        <button type="button" class="gate-home__plate-button" @click="$router.push('/my-home/car-number')" :aria-label="vehicle.name || vehicle.plate_number"><HomeLicensePlate :number="vehicle.plate_number" /></button>
                        <figcaption>{{ vehicle.name || $t('myHome.vehicle', { number: index + 1 }) }}</figcaption>
                    </figure>
                    <figure v-if="capacity.canAddVehicle" class="gate-home__vehicle" :aria-label="$t('myHome.addVehicle')">
                        <button type="button" class="gate-home__plate-button" @click="$router.push('/my-home/car-number')" :aria-label="$t('myHome.addVehicle')"><HomeLicensePlate empty /></button>
                        <figcaption>{{ $t('myHome.addVehicle') }}</figcaption>
                    </figure>
                </div>
                <button type="button" class="gate-home__edit" @click="$router.push('/my-home/car-number')">{{ $t('myHome.edit') }} <svg viewBox="0 0 10 16" aria-hidden="true"><path d="M9.75 7.79422L0 0V15.5884Z" fill="currentColor" /></svg></button>
            </section>
        </template>
    </main>
    </div>
</template>

<script>
import { ref, computed, watch } from 'vue'
import { useMainStore } from '@/store/mainStore'
import ApiManager from '@/services/ApiManager'
import { getHomeAuthToken, getHomeCapacity } from '@/services/myHome'
import HomeLicensePlate from '@/components/HomeLicensePlate.vue'
import MyHomePageHeader from '@/components/MyHomePageHeader.vue'

export default {
    name: 'MyHomeView',
    components: { HomeLicensePlate, MyHomePageHeader },
    setup() {
        const store = useMainStore()
        const home = ref(null)
        const loading = ref(true)
        const error = ref(null)
        const images = ref({})
        const retryKey = ref(0)
        const capacity = computed(() => getHomeCapacity(home.value))
        watch(() => [store.login, retryKey.value], async ([login], previous, onCleanup) => {
            home.value = null
            images.value = {}
            error.value = null
            loading.value = true
            let active = true
            const urls = []
            const controller = new AbortController()
            onCleanup(() => {
                active = false
                controller.abort()
                urls.forEach(url => URL.revokeObjectURL(url))
            })
            if (!login) {
                loading.value = false
                error.value = 'myHome.noAccess'
                return
            }
            try {
                const token = await getHomeAuthToken(login)
                if (!active) return
                const result = await ApiManager.getMyHomeDetails(token, controller.signal)
                if (!active) return
                home.value = result.data
                loading.value = false
                if (!home.value.has_access) return
                await Promise.all(home.value.photos.map(async photo => {
                    try {
                        const response = await ApiManager.getMyHomePhoto(token, photo.id, controller.signal)
                        if (!active) return
                        const url = URL.createObjectURL(response.data)
                        urls.push(url)
                        images.value = { ...images.value, [photo.id]: url }
                    } catch (photoError) {
                        // Keep the profile name visible when its image cannot be loaded.
                    }
                }))
            } catch (requestError) {
                if (active) {
                    const status = requestError.response?.status
                    error.value = status === 409 ? 'myHome.ambiguous' :
                        [401, 403, 404].includes(status) ? 'myHome.noAccess' : 'myHome.unavailable'
                    loading.value = false
                }
            }
        }, { immediate: true, flush: 'sync' })
        return { home, loading, error, images, capacity, retryKey }
    }
}
</script>

<style scoped>
.gate-home button { font-family: inherit; font-size: 15px; border: 0; padding: 0; background: transparent; }
.gate-home button:disabled { opacity: 1; cursor: default; }
.gate-home__edit svg { display: block; flex: 0 0 10px; width: 10px; height: 16px; }
.gate-home__address { display: grid; grid-template-columns: 42px minmax(0, 1fr); gap: 13px 8px; align-items: center; }
.gate-home__address dt { font-family: 'Montserrat', sans-serif; font-weight: 500; font-size: 10px; line-height: 12px; }
.gate-home__address dd { margin: 0; font-size: 13px; line-height: 14px; overflow-wrap: anywhere; }
.gate-home .gate-home__accesses { position: relative; display: block; width: calc(100% - 9px); height: 28px; margin: 40px 0 0 2px; color: #1b1c22; font-size: 13px; line-height: 14px; }
.gate-home__accesses::before { content: ''; position: absolute; inset: 3px; background: rgb(129 202 8 / .7); pointer-events: none; }
.gate-home__accesses span { position: relative; display: inline-block; transform: translate(2px, 1px); }
.gate-home__accesses::after { content: ''; position: absolute; inset: 0; pointer-events: none; background: linear-gradient(var(--home-green),var(--home-green)) left top/8px 1px no-repeat, linear-gradient(var(--home-green),var(--home-green)) right top/8px 1px no-repeat, linear-gradient(var(--home-green),var(--home-green)) left bottom/8px 1px no-repeat, linear-gradient(var(--home-green),var(--home-green)) right bottom/8px 1px no-repeat, linear-gradient(var(--home-green),var(--home-green)) left top/1px 8px no-repeat, linear-gradient(var(--home-green),var(--home-green)) right top/1px 8px no-repeat, linear-gradient(var(--home-green),var(--home-green)) left bottom/1px 8px no-repeat, linear-gradient(var(--home-green),var(--home-green)) right bottom/1px 8px no-repeat; }
.gate-home__section { margin-top: 40px; }
.gate-home__section h2 { text-align: center; color: var(--home-green); font-size: 13px; line-height: 14px; letter-spacing: .1em; font-weight: 400; margin: 0 0 7px; }
.gate-home__description { font-family: 'Montserrat', sans-serif; font-weight: 500; font-size: 10px; line-height: 12px; color: var(--home-muted); margin: 0 0 17px; }
.gate-home__profiles { display: grid; grid-template-columns: repeat(3, 90px); justify-content: space-between; gap: 20px 12px; padding: 0 6px 0 3px; }
.gate-home figure { margin: 0; min-width: 0; }
.gate-home__profile { width: 90px; }
.gate-home__portrait { display: block; width: 100%; aspect-ratio: 1; position: relative; }
.gate-home__portrait::before, .gate-home__portrait::after { content: ''; position: absolute; inset: 0; pointer-events: none; background: linear-gradient(#cacaca, #cacaca) left top/8px 1px no-repeat, linear-gradient(#cacaca, #cacaca) right top/8px 1px no-repeat, linear-gradient(#cacaca, #cacaca) left bottom/8px 1px no-repeat, linear-gradient(#cacaca, #cacaca) right bottom/8px 1px no-repeat; }
.gate-home__portrait::after { transform: rotate(90deg); }
.gate-home__portrait img, .gate-home__portrait svg { position: absolute; top: 3px; left: 3px; width: calc(100% - 6px); height: calc(100% - 6px); object-fit: cover; background: rgb(202 202 202 / .1); }
.gate-home__portrait svg { fill: none; stroke: var(--home-muted); stroke-width: 2; }
.gate-home__plus svg { top: 0; left: 0; width: 100%; height: 100%; background: transparent; stroke: #cacaca; stroke-width: 4; }
.gate-home figcaption { text-align: center; color: var(--home-muted); font-family: 'Montserrat', sans-serif; font-weight: 500; font-size: 11px; line-height: 13px; margin-top: 6px; overflow-wrap: anywhere; }
.gate-home .gate-home__edit { display: flex; align-items: center; gap: 6px; height: 18px; margin: 15px 6px 0 auto; color: #cc1255; font-size: 15px; line-height: 16px; }
.gate-home__plate-button { display: block; width: 100%; cursor: pointer; }
.gate-home__portrait, .gate-home__edit, .gate-home__accesses { cursor: pointer; }
.gate-home__section + .gate-home__section { margin-left: 1px; }
.gate-home__section + .gate-home__section h2 { margin-bottom: 8px; }
.gate-home__section + .gate-home__section .gate-home__description { min-height: 24px; margin-bottom: 12px; }
.gate-home__vehicles { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 15px 12px; padding: 0 16px 0 12px; }
.gate-home__vehicles :deep(.home-license-plate) { aspect-ratio: 157 / 36; }
.gate-home__vehicles :deep(.home-license-plate__number) { font-size: 15px; color: #040404; }
.gate-home__status { margin: 40px 0; font-family: 'Montserrat', sans-serif; font-size: 13px; line-height: 20px; text-align: center; }
.gate-home .gate-home__retry { color: var(--home-green); margin-top: 18px; cursor: pointer; }
@media (max-width: 340px) { .gate-home__profiles { grid-template-columns: repeat(3, minmax(0, 1fr)); } .gate-home__profile { width: 100%; } .gate-home__vehicles { padding: 0; gap: 15px 10px; } }
</style>
